import { browser } from '$app/environment';
import { page } from '$app/stores';
import { PUBLIC_BETWATCH_API_ENDPOINT } from '$env/static/public';
import type { LayoutConfig, LayoutPreference, SiteConfig } from '$types/races';
import { derived, get, writable } from 'svelte/store';
import { Bookmaker } from './bookmakers';

export function shortenBookmakerName(name: string): string {
  if (!name) return name;
  name = name.replace('bookmaking', '');
  name = name.replace('Southerncross', 'SC');
  name = name.replace('Colossalbet', 'Colossal');
  name = name.replace('Marantellibet', 'Marantelli');
  name = name.replace('Woodcockracing', 'Woodcock');
  name = name.replace('Robwaterhouse', 'Rob WH');
  name = name.replace('Pendleburybet', 'Pendlebury');
  name = name.replace('Webetnet', 'Webet');
  name = name.replace('Vipbettingservices', 'VIP');
  return name;
}

export const sidebarOpen = writable(false);

export const defaultLayoutConfigFixedWin = {
  name: 'Default Win',
  scratchedRunnerVisibility: 'BOTTOM',
  showRemainingBookmakers: 'FIXED_WIN',
  remainingBookmakerSort: 'MARKET_FRAME',
  //   need to add these to schema
  //   runnerSort: 'PRICE_ASC',
  //   runnerPriceSortBookmaker: Bookmaker.TAB,
  //   end add to schema
  boldTopPrice: false,
  layoutPreferences: [
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'BACK',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_WIN'
    },
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'LAY',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_WIN'
    },
    {
      bookmaker: Bookmaker.TAB,
      locked: true,
      type: 'FIXED_WIN_FLUCS',
      period: 'ALL'
    },
    {
      type: 'BEST_PRICE_WIN',
      locked: true
    }
  ] as Array<LayoutPreference>
} as LayoutConfig;

export const defaultLayoutConfigFixedPlace = {
  name: 'Default Place',
  scratchedRunnerVisibility: 'BOTTOM',
  showRemainingBookmakers: 'FIXED_PLACE',
  remainingBookmakerSort: 'MARKET_FRAME',
  //   need to add these to schema
  runnerSort: 'BARRIER',
  runnerPriceSortBookmaker: Bookmaker.TAB,
  //   end add to schema
  boldTopPrice: false,
  layoutPreferences: [
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'BACK',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_PLACE'
    },
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'LAY',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_PLACE'
    },
    {
      bookmaker: Bookmaker.TAB,
      locked: true,
      type: 'FIXED_PLACE_FLUCS'
    },
    {
      type: 'BEST_PRICE_PLACE',
      locked: true
    }
  ] as Array<LayoutPreference>
} as LayoutConfig;

export const defaultFreeLayoutConfigFixedWin = {
  name: 'Default Win',
  scratchedRunnerVisibility: 'BOTTOM',
  showRemainingBookmakers: 'FIXED_WIN',
  remainingBookmakerSort: 'MARKET_FRAME',
  //   need to add these to schema
  runnerSort: 'BARRIER',
  runnerPriceSortBookmaker: Bookmaker.TAB,
  //   end add to schema
  boldTopPrice: false,
  layoutPreferences: [
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'BACK',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_WIN'
    },
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'LAY',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_WIN'
    },
    {
      type: 'BEST_PRICE_WIN',
      locked: true
    }
  ] as Array<LayoutPreference>
} as LayoutConfig;

export const defaultFreeLayoutConfigFixedPlace = {
  name: 'Default Place',
  scratchedRunnerVisibility: 'BOTTOM',
  showRemainingBookmakers: 'FIXED_PLACE',
  remainingBookmakerSort: 'MARKET_FRAME',
  //   need to add these to schema
  runnerSort: 'BARRIER',
  runnerPriceSortBookmaker: Bookmaker.TAB,
  //   end add to schema
  boldTopPrice: false,
  layoutPreferences: [
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'BACK',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_PLACE'
    },
    {
      bookmaker: Bookmaker.BETFAIR,
      side: 'LAY',
      level: 'ZERO',
      locked: true,
      type: 'FIXED_PLACE'
    },
    {
      type: 'BEST_PRICE_PLACE',
      locked: true
    }
  ] as Array<LayoutPreference>
} as LayoutConfig;

export function createConfigStore({ isFree = false } = {}) {
  const storedLayoutConfigName = browser
    ? localStorage.getItem('activeLayoutConfig') ?? 'Default Win'
    : 'Default Win';

  const activeLayoutConfigName = writable(storedLayoutConfigName);
  browser &&
    activeLayoutConfigName.subscribe((layoutConfigName) => {
      layoutConfigName
        ? localStorage.setItem('activeLayoutConfig', layoutConfigName)
        : null;
    });

  let layouts = [
    defaultFreeLayoutConfigFixedWin,
    defaultFreeLayoutConfigFixedPlace
  ];
  if (!isFree) {
    layouts = [defaultLayoutConfigFixedWin, defaultLayoutConfigFixedPlace];
  }

  const configs = writable(new Array<LayoutConfig>(...layouts));
  const { subscribe, set, update } = configs;

  const activeLayoutConfig = derived(
    [activeLayoutConfigName, configs, page],
    ([$activeLayoutConfigName, $configs]) => {
      return (
        $configs.find((config) => config.name === $activeLayoutConfigName) ??
        $configs[0]
      );
    }
  );

  async function updateUserLayouts({ force }: { force?: boolean } = {}) {
    if (!browser) return;
    const access_token = get(page)?.data?.session?.access_token;
    if (!access_token) return;

    // get all layouts except for the default ones
    const nonDefaultLayouts = get(configs).filter(
      (config) =>
        config.name !== defaultLayoutConfigFixedWin.name &&
        config.name !== defaultLayoutConfigFixedPlace.name
    );
    // dont override if it's only the default layouts
    if (nonDefaultLayouts.length === 0 && !force) return;

    const response = await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + access_token
      },
      body: JSON.stringify({
        query: `mutation UpdateUserLayouts($layouts: UserLayoutsInput!) {
      updateUserLayouts(layouts: $layouts) {
        boldTopPrice
        layoutPreferences {
          bookmaker
          level
          locked
          side
          type
        }
        name
        remainingBookmakerSort
        scratchedRunnerVisibility
        showRemainingBookmakers
        runnerSort
        runnerPriceSortBookmaker
      }
    }`,
        variables: { layouts: { layouts: nonDefaultLayouts } }
      })
    });
    if (!response.ok) {
      throw new Error('Failed to update user layouts');
    }
  }

  function addOne(layoutConfig: LayoutConfig, { mutate = true } = {}) {
    // ensure that the layoutConfig does not have any duplicate layoutPreferences
    // if there is, remove the duplicate
    const layoutPreferences = layoutConfig.layoutPreferences;
    const uniqueLayoutPreferences = layoutPreferences.filter(
      (layoutPreference, index) =>
        layoutPreferences.findIndex(
          (lp) =>
            lp.bookmaker === layoutPreference.bookmaker &&
            lp.side === layoutPreference.side &&
            lp.level === layoutPreference.level &&
            lp.type === layoutPreference.type &&
            lp.name === layoutPreference.name
        ) === index
    );

    // mutate if there were duplicates
    if (uniqueLayoutPreferences.length !== layoutPreferences.length) {
      mutate = true;
    }

    layoutConfig.layoutPreferences = uniqueLayoutPreferences;

    update((configs) => [...configs, layoutConfig]);
    if (mutate) updateUserLayouts();
  }

  function updateOne(layoutConfig: LayoutConfig) {
    update((configs) => {
      const index = configs.findIndex(
        (config) => config.name === layoutConfig.name
      );
      if (index === -1) {
        throw new Error('Layout not found');
      }
      configs[index] = layoutConfig;
      updateUserLayouts();
      return configs;
    });
  }

  function replaceOne(layoutConfigName: string, layoutConfig: LayoutConfig) {
    update((configs) => {
      // ignore if new is same as old
      if (
        JSON.stringify(
          configs.find((config) => config.name === layoutConfig.name)
        ) === JSON.stringify(layoutConfig)
      ) {
        return configs;
      }

      const index = configs.findIndex(
        (config) => config.name === layoutConfigName
      );

      // make sure there is no other layout with the same name
      if (configs.find((config) => config.name === layoutConfig.name)) {
        throw new Error('Layout name already exists');
      }
      if (!layoutConfigName) {
        throw new Error('Layout name cannot be empty');
      }

      //   add it as a new layout (with new name), then remove the old one
      configs.push(layoutConfig);
      //   set active layout to the new one
      activeLayoutConfigName.set(layoutConfig.name);
      configs.splice(index, 1);

      updateUserLayouts();
      return configs;
    });
  }

  function updateLayoutPreferences(
    layoutName: string,
    layoutPreferences: Array<LayoutPreference>
  ) {
    update((configs) => {
      const activeLayoutConfig = configs.find(
        (config) => config.name === layoutName
      );
      if (activeLayoutConfig) {
        activeLayoutConfig.layoutPreferences = layoutPreferences;
        //   only update if this is not a default layout
        if (activeLayoutConfig.name !== defaultLayoutConfigFixedWin.name) {
          updateUserLayouts();
        }
      }
      return configs;
    });
  }

  function updateLayoutPreference(
    layoutName: string,
    layoutPreference: LayoutPreference
  ) {
    // find the relevant layout preference and update it
    update((configs) => {
      const activeLayoutConfig = configs.find(
        (config) => config.name === layoutName
      );
      if (activeLayoutConfig) {
        const index = activeLayoutConfig.layoutPreferences.findIndex(
          (preference) =>
            preference.type === layoutPreference.type &&
            preference.bookmaker === layoutPreference.bookmaker &&
            preference.side === layoutPreference.side &&
            preference.level === layoutPreference.level &&
            preference.name === layoutPreference.name &&
            preference.size === layoutPreference.size &&
            preference.period === layoutPreference.period
        );
        if (index !== -1) {
          activeLayoutConfig.layoutPreferences[index] = layoutPreference;
          //   only update if this is not a default layout
          if (activeLayoutConfig.name !== defaultLayoutConfigFixedWin.name) {
            updateUserLayouts();
          }
        }
      }
      return configs;
    });
  }

  function removeOne(layoutConfigName: string) {
    update((configs) => {
      const previousLength = configs.length;
      const index = configs.findIndex(
        (config) => config.name === layoutConfigName
      );

      if (index === -1) return configs;
      configs.splice(index, 1);
      //   force removal of last layout if it was in fact the last one  (3=2 defaults + 1 custom)

      updateUserLayouts({ force: previousLength === 3 });
      return configs;
    });
  }

  function removeLayoutPreference(
    layoutName: string,
    layoutPreference: LayoutPreference
  ) {
    update((configs) => {
      const activeLayoutConfig = configs.find(
        (config) => config.name === layoutName
      );
      if (activeLayoutConfig) {
        const index = activeLayoutConfig.layoutPreferences.findIndex(
          (preference) =>
            preference.type === layoutPreference.type &&
            preference.bookmaker === layoutPreference.bookmaker &&
            preference.side === layoutPreference.side &&
            preference.level === layoutPreference.level &&
            preference.size === layoutPreference.size &&
            preference.name === layoutPreference.name &&
            preference.period === layoutPreference.period
        );
        if (index !== -1) {
          activeLayoutConfig.layoutPreferences.splice(index, 1);
          updateUserLayouts();
        }
      }
      return configs;
    });
  }

  function isActiveDefault() {
    const name = get(activeLayoutConfigName);
    return (
      name === defaultLayoutConfigFixedWin.name ||
      name === defaultLayoutConfigFixedPlace.name
    );
  }

  return {
    subscribe,
    set,
    update,
    hasConfig: (layoutConfigName: string) =>
      get(configs).some((config) => config.name === layoutConfigName),
    getOne: (layoutConfigName: string) =>
      get(configs).find((config) => config.name === layoutConfigName),
    getAllNonDefault: () =>
      get(configs).filter(
        (config) =>
          config.name !== defaultLayoutConfigFixedWin.name &&
          config.name !== defaultLayoutConfigFixedPlace.name
      ),
    addOne,
    updateOne,
    updateLayoutPreferences,
    removeLayoutPreference,
    removeOne,
    setActiveLayoutConfig: (layoutConfigName: string) =>
      activeLayoutConfigName.set(layoutConfigName),
    activeLayoutConfigName,
    activeLayoutConfig,
    updateUserLayouts,
    replaceOne,
    updateLayoutPreference,
    isActiveDefault
  };
}

export async function updateAlertConfig() {
  if (!browser) return;
  const access_token = get(page)?.data?.session?.access_token;
  if (!access_token) return;

  const user = get(page).data?.user?.data?.user;
  if (!user) return;

  const response = await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },
    body: JSON.stringify({
      query: `mutation UpdateAlertConfig($config: AlertConfigInput!) {
            updateAlertConfig(config: $config) {
              telegramId
              alertSound
              email
              discordWebhookUrl
        }
      }`,
      variables: { config: user.alertConfig }
    })
  });
  if (!response.ok) {
    throw new Error('Failed to update user alert config');
  }
}

export async function updateSiteConfig() {
  if (!browser) return;
  const access_token = get(page)?.data?.session?.access_token;
  if (!access_token) return;

  const user = get(page).data?.user?.data?.user;
  if (!user) return;

  const response = await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },
    body: JSON.stringify({
      query: `mutation UpdateSiteConfig($config: SiteConfigInput!) {
        updateUserSiteConfig(config: $config) {
            siteConfig {
                hideAbandonedRaces
                alternateColourMode
                autoEventNav
                relativeEventTime
                betfairReductionFactor
                prefill {
                  quickFill
                  defaults {
                    thoroughbred {
                      type value
                    }
                    harness {
                      type value
                    }
                    greyhound {
                      type value
                    }
                  }
                }
              }
      }
    }`,
      variables: { config: user.siteConfig }
    })
  });
  if (!response.ok) {
    throw new Error('Failed to update user site config');
  }
}

export const activeSiteConfig = derived(page, ($page) => {
  return $page?.data?.user?.data?.user?.siteConfig ?? ({} as SiteConfig);
});

// keep track of the last time the user clicked on the prefill button
// and an alert was shown, so we don't show it again for a while
export const lastBetslipPrefillAlert = writable(0);
